import type Listing from 'data/listing';
import { PriceHistoryRecord } from 'domain/listing/value-objects/price-history';
import { OpenHouseVO } from 'domain/listing/value-objects/open-house';
import { isNewListing } from 'ui/recency';
import { isComingSoon } from 'types/reso';
import type { OpenHouse } from 'data/listing';
import type { StandardStatus } from '@zoocasa/go-search';

/**
 * Badge tier (priority order)
 * Lower number = higher priority
 */
export enum BadgeTier {
  STATUS = 1, // Coming Soon (and future status badges) — highest priority
  MOMENTUM = 2, // New, Price Reduced
  ACTION = 3, // Open House, Virtual Tour
  ATTRIBUTE = 4 // Future (Waterfront, etc.)
}

/**
 * Badge selection result
 * Contains the domain value objects that should be displayed as badges.
 * Only ONE field will be set per selection.
 */
export interface BadgeSelection {
  tier: BadgeTier;

  // Tier 1: Status
  /** True when the listing is Coming Soon (drives the badge regardless of date) */
  comingSoon?: boolean;
  /** Optional go-live date (onMarketDate); when absent the badge shows no date */
  comingSoonDate?: Date;
  /** True when a Coming Soon listing should also show the eXp Early Access chip */
  expEarlyAccess?: boolean;

  // Tier 2: Momentum
  /** Listing added date for "New" badge */
  addedAt?: Date;
  priceHistory?: PriceHistoryRecord;

  // Tier 3: Action
  openHouse?: OpenHouse;
  virtualTourUrl?: string;
}

/**
 * Provider ID for eXp Early Access listings — Coming Soon listings from this
 * provider show an additional "eXp Early Access" chip alongside the Coming
 * Soon badge.
 */
export const EXP_EARLY_ACCESS_PROVIDER_ID = 31449;

/**
 * Returns an "eXp Early Access" badge selection when the listing is Coming
 * Soon AND sourced from the eXp Early Access provider, otherwise null.
 * Shares tier STATUS with the Coming Soon badge so it always renders
 * alongside it (see BadgeDisplayPolicy.selectSlot1/selectSlot2 and
 * ComingSoonBadge, which both consume this).
 *
 * @param providerId - The listing's provider ID
 * @param status - The listing's standard status
 */
export function getExpEarlyAccessSelection(
  providerId: number | null | undefined,
  status: StandardStatus | null | undefined
): BadgeSelection | null {
  if (providerId !== EXP_EARLY_ACCESS_PROVIDER_ID || status == null || !isComingSoon(status)) {
    return null;
  }
  return {
    tier: BadgeTier.STATUS,
    expEarlyAccess: true,
  };
}

/**
 * Returns a "Coming Soon" badge selection when the listing status is Coming Soon,
 * otherwise null. Shared by BadgeDisplayPolicy (address gallery) and the
 * ComingSoonBadge component (listing cards, map cluster cards) so the gating
 * lives in exactly one place.
 *
 * The go-live date (`onMarketDate`) is optional: when it's missing or invalid,
 * the badge still renders, just without a date ("Coming Soon").
 *
 * @param status - The listing's standard status
 * @param onMarketDate - The listing's go-live date, if known
 */
export function getComingSoonSelection(
  status: StandardStatus | null | undefined,
  onMarketDate?: Date | string | null
): BadgeSelection | null {
  if (status == null || !isComingSoon(status)) {
    return null;
  }
  // Accept a Date (in-memory) or an ISO string (after SSR serialization).
  const parsed = onMarketDate ? new Date(onMarketDate) : null;
  const isValidDate = parsed !== null && !Number.isNaN(parsed.getTime());
  return {
    tier: BadgeTier.STATUS,
    comingSoon: true,
    comingSoonDate: isValidDate ? parsed : undefined,
  };
}

/**
 * BadgeDisplayPolicy
 * 
 * UI Policy that implements the slot-based badge priority system.
 * Returns VALUE OBJECTS that should be displayed, not UI components.
 * 
 * Business Rules (Slot-Based Logic):
 * - Max 2 badges shown (Slot 1 + Slot 2)
 * - Slot 1 (Deal Breaker): Status > New/Price Reduced > Open House
 * - Slot 2 (Hook): Open House > Virtual Tour > Attributes
 * - New listing threshold: 7 days
 * - Status badges: ONLY for non-active (Sold, Pending, Expired, etc.)
 * - Active listings (For Sale/For Rent) do NOT show status badge
 * 
 * Product Decision: https://coda.io/d/Zoocasa-eXprealty-com_dT-0uNbKbyy/Decision-Ideas_suT26vud
 */
export class BadgeDisplayPolicy {
  private readonly NEW_LISTING_THRESHOLD_DAYS = 7;

  /**
   * Selects which value objects should be displayed as badges.
   * 
   * Uses slot-based logic:
   * - Slot 1: Status OR New/Price Reduced OR Open House (in that order)
   * - Slot 2: Highest priority remaining (Open House > Virtual Tour > Attributes)
   * 
   * @param listing - The listing to get badges for
   * @returns Array of badge selections (max 2), in slot order [Slot1, Slot2]
   * 
   * @example
   * ```typescript
   * const policy = new BadgeDisplayPolicy();
   * const badges = policy.selectBadges(listing);
   * // Returns: [{ tier: 2, addedAt: Date }, { tier: 3, openHouse: {...} }]
   * ```
   */
  selectBadges(listing: Listing): BadgeSelection[] {
    const candidates = this.collectCandidates(listing);
    return this.prioritize(candidates);
  }

  /**
   * Collects all applicable badge candidates from listing data.
   * Business logic: What badges should be considered?
   */
  private collectCandidates(listing: Listing): BadgeSelection[] {
    const badges: BadgeSelection[] = [];

    // Tier 1: Status (Coming Soon) — gated on real status, dated by the real
    // go-live date (onMarketDate) when available, otherwise dateless.
    const comingSoon = getComingSoonSelection(listing.standardStatus, listing.onMarketDate);
    if (comingSoon) {
      badges.push(comingSoon);
    }

    // Tier 1: eXp Early Access — Coming Soon listings from provider 31449 also
    // get this chip. Same tier as Coming Soon so it wins Slot 2 over New/Open
    // House/Virtual Tour (see selectSlot2's tier sort).
    const expEarlyAccess = getExpEarlyAccessSelection(listing.providerId, listing.standardStatus);
    if (expEarlyAccess) {
      badges.push(expEarlyAccess);
    }

    // Tier 1: Recency (New Listing)
    // Uses stricter threshold (7 days) for badge display
    if (isNewListing(listing.addedAt, this.NEW_LISTING_THRESHOLD_DAYS)) {
      badges.push({ tier: BadgeTier.MOMENTUM, addedAt: listing.addedAt });
    }

    // Tier 2: Price History (Price Reduced)
    // PENDING: Backend implementation required
    // 
    // Current state:
    // - listing.lastStatus === 'Pc' indicates price changed (but not direction)
    // - listing.price exists (current price)
    // - listing.previousPrice does NOT exist (needed to confirm reduction)
    // - listing.priceChangedAt does NOT exist (needed for timestamp)
    // 
    // Note: Cannot use price history array - that's for different time periods
    // (e.g., listing sold in 2023 vs. listing in 2024), not price changes of
    // the current listing.
    // 
    // Options being considered:
    // 1. Add previousPrice and priceChangedAt to Listing type (backend change) - RECOMMENDED
    // 2. Use lastStatus === 'Pc' for generic "Price Changed" badge (can't guarantee reduction)
    // 
    // Decision: Waiting for team decision on approach
    // 
    // const priceHistory = PriceHistoryRecord.from({
    //   previousPrice: listing.previousPrice, // ❌ Does not exist
    //   currentPrice: listing.price,
    //   priceChangedAt: listing.priceChangedAt, // ❌ Does not exist
    // });
    // if (priceHistory?.hasReduction) {
    //   badges.push({ tier: BadgeTier.MOMENTUM, priceHistory });
    // }

    // Tier 3: Open House
    // Only show badge if there is at least one FUTURE open house
    // Uses OpenHouseVO.filterFuture() for consistent date filtering
    const futureOpenHouses = OpenHouseVO.filterFuture(listing.openHouses);
    if (futureOpenHouses.length > 0) {
      badges.push({ 
        tier: BadgeTier.ACTION, 
        openHouse: futureOpenHouses[0].raw, 
      });
    }

    // Tier 3: Virtual Tour
    if (listing.virtualTourUrl) {
      badges.push({ 
        tier: BadgeTier.ACTION, 
        virtualTourUrl: listing.virtualTourUrl, 
      });
    }

    return badges;
  }

  /**
   * Prioritizes badges using slot-based logic.
   * 
   * Business logic from Coda:
   * - Slot 1 (Deal Breaker): Status > New/Price Reduced > Open House
   * - Slot 2 (Hook): Open House > Virtual Tour > Attributes
   * 
   * @see https://coda.io/d/Zoocasa-eXprealty-com_dT-0uNbKbyy/Decision-Ideas_suT26vud
   */
  private prioritize(badges: BadgeSelection[]): BadgeSelection[] {
    const result: BadgeSelection[] = [];
    
    // Slot 1: The "Deal Breaker" Slot
    const slot1 = this.selectSlot1(badges);
    if (slot1) {
      result.push(slot1);
    }
    
    // Slot 2: The "Hook" Slot (highest priority remaining)
    const slot2 = this.selectSlot2(badges, slot1);
    if (slot2) {
      result.push(slot2);
    }
    
    return result;
  }
  
  /**
   * Slot 1: The "Deal Breaker" Slot
   * Priority: New/Price Reduced > Open House
   */
  private selectSlot1(badges: BadgeSelection[]): BadgeSelection | null {
    // 0. Status (Coming Soon) always wins Slot 1 when present
    const status = badges.find(b => b.tier === BadgeTier.STATUS);
    if (status) {
      return status;
    }

    // 1. Check Tier 2 (New or Price Reduced)
    const momentum = badges.find(b => b.tier === BadgeTier.MOMENTUM);
    if (momentum) {
      return momentum;
    }
    
    // 2. Fallback to Open House (Tier 3)
    const openHouse = badges.find(b => b.openHouse);
    if (openHouse) {
      return openHouse;
    }
    
    return null;
  }
  
  /**
   * Slot 2: The "Hook" Slot
   * Highest priority remaining badge (not used in Slot 1)
   * 
   * Priority order:
   * - Tier 2 (New/Price Reduced) if not in Slot 1
   * - Tier 3: Open House > Virtual Tour
   * - Tier 4: Attributes (future)
   */
  private selectSlot2(badges: BadgeSelection[], slot1: BadgeSelection | null): BadgeSelection | null {
    // Filter out the badge already used in Slot 1
    const remaining = badges.filter(b => b !== slot1);
    
    if (remaining.length === 0) {
      return null;
    }
    
    // Return highest priority remaining (lowest tier number)
    return remaining.sort((a, b) => {
      // First sort by tier
      if (a.tier !== b.tier) {
        return a.tier - b.tier;
      }
      
      // Within Tier 3, prioritize Open House over Virtual Tour
      if (a.tier === BadgeTier.ACTION && b.tier === BadgeTier.ACTION) {
        if (a.openHouse) return -1;
        if (b.openHouse) return 1;
      }
      
      return 0;
    })[0];
  }
}
